import Swal from "sweetalert2";

// Returned when the admin cancels the email-change verification (nothing was saved).
export const EMAIL_CHANGE_CANCELLED = "EMAIL_CHANGE_CANCELLED";

type OtpExtra = { emailOtp?: string; resendOtp?: boolean };

const isOtpResponse = (r: any) => r && typeof r === "object" && typeof r.emailOtp === "string";

/**
 * Wraps an "edit user" API call. If the backend reports that the email address was
 * changed, it asks for the 6-digit code mailed to the NEW address and repeats the
 * same call with that code. Resolves with the final backend response, or
 * EMAIL_CHANGE_CANCELLED if the admin gives up.
 */
export async function withEmailOtp(call: (extra: OtpExtra) => Promise<any>): Promise<any> {
  let res = await call({});

  while (isOtpResponse(res)) {
    if (res.emailOtp === "failed") {
      await Swal.fire({ title: "Email verification", text: res.message, icon: "error", allowOutsideClick: false });
      return EMAIL_CHANGE_CANCELLED;
    }

    const result = await Swal.fire({
      title: "Verify new email address",
      text: res.message,
      icon: res.emailOtp === "required" ? "info" : "warning",
      input: "text",
      inputPlaceholder: "Enter 6-digit code",
      inputAttributes: { maxlength: "6", inputmode: "numeric", autocomplete: "one-time-code" },
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: "Verify",
      denyButtonText: "Resend code",
      cancelButtonText: "Cancel",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showLoaderOnConfirm: true,
      inputValidator: (value) => (/^\d{6}$/.test((value || "").trim()) ? null : "Enter the 6-digit code"),
      preConfirm: async (otp: string) => {
        try {
          const r = await call({ emailOtp: otp.trim() });
          if (isOtpResponse(r) && r.emailOtp === "invalid") {
            Swal.showValidationMessage(r.message);
            return false;
          }
          return r;
        } catch {
          Swal.showValidationMessage("Unable to reach the server. Please try again.");
          return false;
        }
      },
    });

    if (result.isConfirmed) {
      res = result.value;               // saved response, or "expired" -> prompt again
    } else if (result.isDenied) {
      res = await call({ resendOtp: true });
    } else {
      return EMAIL_CHANGE_CANCELLED;
    }
  }
  return res;
}
